import { useMemo } from "react";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Trash2 } from "lucide-react";
import {
  TextInput,
  TextInputWithPrefix,
  Button,
} from "../ui";
import { computeTierBreakdown, formatCurrency } from "../../_utils/pricing";
import "./_tier-breakdown.scss";

/**
 * Re-derives every tier's `to` boundary from the NEXT tier's `from` (the
 * last tier is always unbounded) - called whenever `from` values or the
 * tier count changes. `to` is deliberately not independently editable (see
 * TierTable's own docstring): letting it drift out of sync with the next
 * tier's `from` would silently create pricing gaps or overlaps. A
 * catalog-sourced tier keeps its own `to` untouched until the user actually
 * edits something - see PlanProductPicker's handleTierFieldChange, which
 * only calls this when the changed field is `from`.
 */
export function withDerivedTierBoundaries(tiers) {
  const sorted = [...(tiers || [])].sort(
    (a, b) => (parseFloat(a?.from) || 0) - (parseFloat(b?.from) || 0),
  );
  return sorted.map((tier, index) => {
    const nextFrom = sorted[index + 1]?.from;
    const to =
      index === sorted.length - 1 || nextFrom === undefined || nextFrom === ""
        ? ""
        : (parseFloat(nextFrom) || 0) - 1;
    return { from: tier.from, to, unitPrice: tier.unitPrice };
  });
}

const columnHelper = createColumnHelper();

/**
 * Editable Volume/Tiered pricing table, nested under a plan/product row.
 * From and Unit price are editable per bracket; To is read-only (see
 * withDerivedTierBoundaries above for why); Qty and Tier price are always
 * computed (via computeTierBreakdown from the current item quantity),
 * never editable - they're a consequence of From/Unit price + quantity,
 * not independent facts you could set inconsistently with the rest.
 *
 * Ported from BillingItems/PlanProductTable's nested tier table (From/Unit
 * price editable, To disabled, Qty/Tier price computed, delete/add tier) -
 * the bracket-splitting calculation itself lives in _utils/pricing.js
 * instead of being reimplemented per-column the way the old table did it.
 *
 * Rendered via TanStack Table (column defs + flexRender) rather than a
 * hand-mapped `.map()`, matching PlanProductPicker/LineItemsEditor/
 * TaxEditor - purely a rendering-mechanism change, same markup/
 * classNames/styling as before. Rows are the COMPUTED breakdown (not raw
 * state), so `getRowId` uses each row's position - same identity
 * `key={index}` already gave it.
 *
 * `rows` MUST be memoized (`useMemo`, not a plain call computed inline in
 * the render body) - TanStack Table's internal auto-reset effects watch
 * `data` BY REFERENCE, and re-derive/reset internal state whenever it
 * looks like "new data" arrived. A fresh array every render (even with
 * identical contents) reads as new data on every pass, which retriggers
 * those effects, which re-renders this component, which computes another
 * fresh array - an infinite render loop with no state actually changing.
 * This is why every other table in this migration sources `data` from
 * stable state/props (or, for DiscountEditor's single synthetic row, an
 * explicit `useMemo`) - TierTable is the only one whose "data" is a pure
 * derivation of its own props rather than state, so it's the one place
 * this had to be memoized explicitly rather than falling out for free.
 */
const TierTable = ({
  itemId,
  tiers = [],
  quantity,
  currencySign,
  onFieldChange,
  onAdd,
  onRemove,
}) => {
  const rows = useMemo(
    () => computeTierBreakdown(tiers, quantity),
    [tiers, quantity],
  );
  const totalPrice = rows.reduce((sum, row) => sum + row.tierPrice, 0);

  const columns = useMemo(
    () => [
      columnHelper.accessor("from", {
        header: "From",
        cell: (info) => (
          <TextInput
            compact
            value={info.getValue()}
            onChange={(value) => onFieldChange(itemId, info.row.index, "from", value)}
          />
        ),
      }),
      columnHelper.accessor("to", {
        header: "To",
        cell: (info) => (
          <TextInput
            compact
            value={info.getValue() === Infinity ? "∞" : info.getValue()}
            disabled
          />
        ),
      }),
      columnHelper.accessor("unitPrice", {
        header: "Unit price",
        cell: (info) => (
          <TextInputWithPrefix
            compact
            prefix={currencySign}
            value={info.getValue()}
            onChange={(value) =>
              onFieldChange(itemId, info.row.index, "unitPrice", value)
            }
          />
        ),
      }),
      columnHelper.accessor("quantityInTier", {
        header: "Qty",
        meta: { className: "tier-breakdown__computed" },
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("tierPrice", {
        header: "Tier price",
        meta: { className: "tier-breakdown__computed" },
        cell: (info) => formatCurrency(info.getValue(), currencySign),
      }),
      columnHelper.display({
        id: "actions",
        header: () => null,
        meta: { className: "tier-breakdown__actions" },
        cell: (info) =>
          info.row.index > 0 && (
            <Button
              variant="ghost-danger"
              size="md"
              iconOnly
              icon={<Trash2 size={16} />}
              onClick={() => onRemove(itemId, info.row.index)}
              aria-label={`Remove tier ${info.row.index + 1}`}
            />
          ),
      }),
    ],
    [itemId, currencySign, onFieldChange, onRemove],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (_row, index) => index,
  });

  return (
    <table className="tier-breakdown">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className={header.column.columnDef.meta?.className}
                aria-hidden={header.id === "actions" || undefined}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                className={cell.column.columnDef.meta?.className}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
        <tr className="tier-breakdown__total-row">
          <td colSpan={3}>Total</td>
          <td className="tier-breakdown__computed">{quantity || 0}</td>
          <td className="tier-breakdown__computed">
            {formatCurrency(totalPrice, currencySign)}
          </td>
          <td />
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={6}>
            <Button variant="ghost" size="md" className="tier-breakdown__add-button" onClick={() => onAdd(itemId)}>
              + Add tier
            </Button>
          </td>
        </tr>
      </tfoot>
    </table>
  );
};

export default TierTable;
